import type { ReactNode } from 'react'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { StreakModal } from '@/components/ui/streak-modal'

interface StreakContextValue {
  triggerStreakEarned: (currentStreak: number) => void
  flameActive: boolean
}

const StreakContext = createContext<StreakContextValue | null>(null)

export function StreakProvider({ children }: { children: ReactNode }) {
  const [flameActive, setFlameActive] = useState(false)
  const [autoModalOpen, setAutoModalOpen] = useState(false)
  const [autoModalStreak, setAutoModalStreak] = useState(0)
  const flameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const triggerStreakEarned = useCallback((currentStreak: number) => {
    setAutoModalStreak(currentStreak)
    setAutoModalOpen(true)
    setFlameActive(true)

    // Clear any existing timer
    if (flameTimerRef.current) {
      clearTimeout(flameTimerRef.current)
    }

    // Auto-expire flame after 15 seconds
    flameTimerRef.current = setTimeout(() => {
      setFlameActive(false)
      flameTimerRef.current = null
    }, 15 * 1000)
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (flameTimerRef.current) {
        clearTimeout(flameTimerRef.current)
      }
    }
  }, [])

  return (
    <StreakContext value={{ triggerStreakEarned, flameActive }}>
      {children}
      <StreakModal
        open={autoModalOpen}
        onClose={() => setAutoModalOpen(false)}
        currentStreak={autoModalStreak}
        earned
      />
    </StreakContext>
  )
}

export function useStreak() {
  const context = use(StreakContext)
  if (!context) {
    throw new Error('useStreak must be used within StreakProvider')
  }
  return context
}
